export const SITE = {
  name: "JD LEGACY GROUP OF COMPANIES CORP.",
  short: "JD Legacy",
  tagline: "BUILD · HOLD · LEGACY",
  blurb:
    "A Philippine holding company investing in technology, real estate, food, and related sectors — one legacy, many crafts.",
  logo: "/logo.webp",
  office: {
    label: "Principal office",
    street: "Unit 1 Dimla St., Purok 6 Anyatam",
    city: "San Ildefonso, Bulacan",
    region: "Region\u00A0III (Central\u00A0Luzon)",
    zip: "3010",
    full: "Unit 1 Dimla St., Purok 6 Anyatam, San Ildefonso, Bulacan, Region\u00A0III (Central\u00A0Luzon), 3010",
    /** Line breaks for the About principal-office block. */
    lines: [
      "Unit 1 Dimla St., Purok 6",
      "Anyatam, San Ildefonso,",
      "Bulacan, Region III",
      "(Central Luzon), 3010",
    ],
    mapsUrl:
      "https://www.google.com/maps/search/?api=1&query=Unit+1+Dimla+St+Purok+6+Anyatam+San+Ildefonso+Bulacan",
  },
} as const;

export const SOCIALS = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61590511281937",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@jdlegacygroup",
  },
] as const;

export const PRIMARY_PURPOSE =
  "To invest in, purchase, or otherwise acquire and own, hold, use, sell, assign, transfer, or otherwise dispose of real and personal property of every kind and description, including but not limited to shares of stock, bonds, debentures, notes, and other securities or obligations of any corporation or associations, domestic or foreign; to possess and exercise in respect thereof all the rights, powers and privileges of ownership, including all voting powers of any stock so owned, without however engaging in dealership of securities or stock brokerage.";

export const SECONDARY_PURPOSES = [
  {
    title: "Group support",
    body: "Provide management, administrative, and technical support services to subsidiary companies and affiliates.",
  },
  {
    title: "Subsidiary investment",
    body: "Organize, promote, and invest in subsidiary corporations engaged in information technology, real estate development, food and beverage services, and other lawful businesses.",
  },
  {
    title: "Intellectual property",
    body: "Own, manage, and protect intellectual property assets, including proprietary software source code, digital systems, and trade secrets.",
  },
  {
    title: "Technology & hardware",
    body: "Engage in software development, IT consultancy, enterprise systems integration, and the maintenance, repair, and distribution of computer hardware, peripherals, and related technological equipment.",
  },
  {
    title: "Real estate",
    body: "Acquire, develop, lease, and hold real estate and landed properties for investment or operations.",
  },
  {
    title: "Food & beverage",
    body: "Operate food service establishments including cafés, bakeries, and restaurants, and manufacture pastry and beverage products.",
  },
] as const;

export const GROUP_COMPANIES = [
  {
    slug: "legacy",
    name: "JD Legacy",
    legalName: "JD LEGACY GROUP OF COMPANIES CORP.",
    focus: "Holding company",
    blurb:
      "Parent company — investing in and supporting subsidiaries across technology, real estate, food, and related sectors.",
    logo: "/logo.webp",
    logoSm: "/logo-32.webp",
    logoMd: "/logo-64.webp",
    href: "/",
    current: true,
  },
  {
    slug: "integrations",
    name: "JD Integrations",
    legalName: "JD Integrations",
    focus: "Technology & systems",
    blurb:
      "Software, ERP, hardware service, and the JD Apps suite — consultancy through bench-side support.",
    logo: "/jd-integrations-logo.webp",
    logoSm: "/jd-integrations-logo-32.webp",
    logoMd: "/jd-integrations-logo-64.webp",
    href: "https://jdintegrations.net",
    current: false,
  },
  {
    slug: "properties",
    name: "JD Properties",
    legalName: "JD Properties",
    focus: "Real estate",
    blurb: "Property and real estate under JD Legacy.",
    logo: "/jd-properties-logo.webp",
    logoSm: "/jd-properties-logo-32.webp",
    logoMd: "/jd-properties-logo-64.webp",
    href: null,
    current: false,
  },
  {
    slug: "delight",
    name: "JD Delight Creations",
    legalName: "JD Delight Creations",
    focus: "Food · cakes & pastries",
    blurb: "Cakes, pastries, and food creations under JD Legacy.",
    logo: "/jd-delight-logo.webp",
    logoSm: "/jd-delight-logo-32.webp",
    logoMd: "/jd-delight-logo-64.webp",
    href: null,
    current: false,
  },
] as const;

export const CONTACT_INTERESTS = [
  "General inquiry",
  "Partnership / referral",
  "JD Integrations",
  "JD Properties",
  "JD Delight Creations",
  "Investment / group services",
] as const;

export const CONSULTATION_TOPICS = [
  "Group / holding company",
  "Investment conversation",
  "Partnership / referral",
  "JD Integrations",
  "JD Properties",
  "JD Delight Creations",
  "Other",
] as const;

/** Core sectors from the AoI — wording stays valid as subsidiaries grow. */
export const FOCUS_SECTORS =
  "technology, real estate, food, and related sectors";

export const COMPANIES_HERO = {
  title: "One group. Many crafts.",
  lead: `${SITE.short} invests in and supports subsidiaries across ${FOCUS_SECTORS} — each company with its own focus, one shared legacy.`,
  portfolioBlurb:
    "The holding company and its operating subsidiaries — each with its own craft under one shared legacy.",
} as const;

export function formatNameList(names: readonly string[]): string {
  const list = [...names];
  if (list.length === 0) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list.at(-1)}`;
}

export const MANIFESTO =
  "Legacy is not one trade — it is many crafts held together. Technology, property, and food each have their floor; the group holds the line.";

export const HERO = {
  support: `${SITE.short} invests in and supports subsidiaries across ${FOCUS_SECTORS}.`,
  cta: { href: "/companies", label: "Explore companies" },
} as const;

export const CAPABILITY_RIBBON = [
  "Holding company",
  "Technology",
  "Real estate",
  "Food & beverage",
  "Group support",
  "Investment",
  "Intellectual property",
  "Subsidiary growth",
] as const;

export const PILLARS = [
  {
    title: "Hold with purpose",
    body: "Primary mandate: invest in and steward shares, securities, and property — not securities dealership.",
  },
  {
    title: "Support the floor",
    body: "Management, admin, and technical support for subsidiaries so each company can focus on its craft.",
  },
  {
    title: "Grow by sector",
    body: "IT, real estate, and food & beverage — organized, promoted, and invested in under one group.",
  },
] as const;
